import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Redirect, Route, Switch, useHistory, useLocation, useRouteMatch} from "react-router-dom";
import {routes, routesLists} from "../../../core/models/static/routes/app-routes";
import OperatorProfileView from "./common/operator-profile";
import PartSupportRequestViews from "./operator/part-support";
import CustomerViews from "./operator/customers";
import OperatorsViews from "./operator/operators";
import AccountingViews from "./operator/accounting";
import SettingViews from "./operator/settings";
import UserNotificationsView from "./operator/user-notifications";
import ContentViews from "./operator/content";
import SalesPersonViews from "./operator/sales-person";
import EnvService from "../../../core/services/env";
import ProductsView from "./operator/products";
import UserPlatformViews from "./operator/user-platform";
import PriceMatricesListView from "./common/price-matrices/list";
import CustomerAccountingViews from "./customer/accounting";
import CustomerGiftCardSummariesView from "./customer/card-summaries/gift-cards";
import CustomerFormResponsesPage from "./customer/forms-management/responses";
import NotificationManagementViews from "./common/notification-management";
import ServerErrorView from "../error/server-error";
import AccessDeniedView from "../error/access-denied";
import {Panel, usePanel} from "../../components/layout";
import WhatsNewDialog from "../../components/dialogs/whats-new";
import CacheService from "../../../core/services/cache/cache_service";
import {useDispatch} from "react-redux";
import Api from "../../../core/services/api/api";
import useIsMounted from "../../hooks/use-is-mounted";
import {setReduxDialogState} from "../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../core/models/static/routes/redux-store-routes";
import LocalStorageService from "../../../core/services/cache/local_storage_service";
import ShippingFeesViews from "./common/shipping-fees";
import PurchaseViews from "./common/purchases";
import VendorsListView from "./operator/vendors";
import VendorViews from "./vendor";
import BirthdayDialog from "../../components/dialogs/birthday";
import FloatingActionButton from "../../components/app-specific/floating-action-button";
import TaskMoreInfoDialog from "../../components/dialogs/task-more-info-dialog";
import NotebookView from "./common/notebook";
import UpsertTaskDialog from "../../components/dialogs/upsert-task-dialog";
import WelcomeToPanelTaskDialog from "../../components/dialogs/welcome-to-panel-task-dialog";
import PrintingView from "./operator/printing";
import FloatingActionButtonNotebookContent from "../../components/app-specific/floating-action-button/notebook-content";
import {matchPath} from "react-router";
import DamageControlViews from "./operator/damage-control";
import SupportViews from "./operator/support";
import UpsertNoteDialog from "../../components/dialogs/upsert-note";
import NCRViews from "./operator/NCR";
import FormManagementViews from "./operator/forms-management";
import BizPointLoyaltyViews from "./operator/biz-point/loyalty";
import BizPointAdminViews from "./operator/biz-point/admin";
import LookupV2Views from "./operator/lookup-v2";
import PanelSidebar from "../../components/panel-sections/sidebar";
import PanelAppbar from "../../components/panel-sections/app-bar";
import './index.scss';
import classnames from "classnames";
import OrderViews from "./common/orders";
import CustomerShoppingCartView from "./customer/shopping-cart";
import QuickOrderDialog from "../../components/dialogs/quick-order-dialog";
import {SuggestiveSellingProvider} from "../../features/suggestive-selling";
import useAccessLevels from "../../hooks/use-access-levels";
import InventoryViews from "./operator/inventory";
import VersionsView from "../versions";
import CustomerCreditCardSummaryView from "./customer/card-summaries/credits";
import {ReduxActions, useAppDispatch, useAppSelector} from "../../redux";
import {useConfigureNotebook, useNotebookConfig} from "../../hooks/use-notebook-config";
import CustomerGeneralInformationViews from "./customer/general-information";
import PromotionsView from "./operator/promotions";
import AnalyticsViews from "./operator/analytics";
import {PanelDrawers} from "../../components/panel-sections/drawers";
import {useOperatorInformation} from "../../hooks/use-operator-information";
import PartnerProfileView from "./common/partner-profile";
import {usePersonalPanelConfigurations} from "../../hooks/use-custom-saved-components-state/use-personal-panel-configurations";
import {usePanelDialogQueue} from "../../hooks/use-panel-dialog-queue";
import CustomerNotesView from "./customer/notes";

const PanelFloatingActionButton = () => {
    const operatorAccessLevels = useAccessLevels();
    const {fabNotesVisible} = useNotebookConfig();
    const customerNotesPageMatch = useRouteMatch({path: routes.customer.notes, exact: true});
    const vendorNotesPageMatch = useRouteMatch({path: routes.vendor.notes, exact: true});
    const showNotes = operatorAccessLevels.packages.taskNote.notes && fabNotesVisible && !customerNotesPageMatch && !vendorNotesPageMatch;
    const showTasks = operatorAccessLevels.packages.taskNote.tasks;

    if (!showNotes && !showTasks) {
        return null;
    }

    return (
        <FloatingActionButton>
            <FloatingActionButtonNotebookContent/>
        </FloatingActionButton>
    );
};

const PanelViews = () => {
    const operatorAccessLevels = useAccessLevels();
    const location = useLocation();
    const {replace: historyReplace} = useHistory();
    const isMounted = useIsMounted();

    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const defaultVendor = useAppSelector(state => state.vendorInformation);

    /**
     * With each change in the url of the application:
     * - if the url is [routes.landing], then changes it to analytics
     */
    useLayoutEffect(() => {
        if (matchPath(location.pathname, {path: routes.landing, exact: true})) {
            if (operatorAccessLevels.packages.dashboard.viewDashboards) {
                historyReplace(routes.analytics.dashboards);
            } else if (operatorAccessLevels.packages.dashboard.systemWideDashboards) {
                historyReplace(routes.analytics.systemDashboards);
            } else if (operatorAccessLevels.packages.lookup.nexusSearch) {
                historyReplace(routes.lookup.base);
            } else if (operatorAccessLevels.packages.base.customerList) {
                historyReplace(routes.customers.base);
            } else if (operatorAccessLevels.packages.sales.salesPerson) {
                historyReplace(routes.salesPerson.dashboard);
            } else if (operatorAccessLevels.packages.warrantyAndReturn.viewList) {
                historyReplace(routes.warrantyAndReturn.requests);
            } else if (operatorAccessLevels.packages.ncr.viewList) {
                historyReplace(routes.ncr.reports);
            } else if (operatorAccessLevels.packages.bizInventory.adjustmentList) {
                historyReplace(routes.inventory.inventoryAdjustment.list);
            } else if (operatorAccessLevels.packages.bizPrint.printHub) {
                historyReplace(routes.printing.printHub);
            } else if (operatorAccessLevels.packages.clientPlatform.webHomepageEditions
                || operatorAccessLevels.packages.clientPlatform.phoneHomepageEditions) {
                historyReplace(routes.userPlatform.list);
            }
        }
    }, [historyReplace, location.pathname, operatorAccessLevels.packages.base.customerList, operatorAccessLevels.packages.bizInventory.adjustmentList, operatorAccessLevels.packages.bizPrint.printHub, operatorAccessLevels.packages.clientPlatform.phoneHomepageEditions, operatorAccessLevels.packages.clientPlatform.webHomepageEditions, operatorAccessLevels.packages.dashboard.systemWideDashboards, operatorAccessLevels.packages.lookup.nexusSearch, operatorAccessLevels.packages.ncr.viewList, operatorAccessLevels.packages.sales.salesPerson, operatorAccessLevels.packages.warrantyAndReturn.viewList, operatorAccessLevels.packages.dashboard.viewDashboards])

    const inNotebookRoute = useMemo(() => matchPath(location.pathname, {path: routes.notebook, exact: true}), [location.pathname]);

    /**
     * Fetches the biz print application token.
     * @type {(function(): Promise<*>)}
     */
    const getBizPrintApplicationToken = useCallback(async () => {
        if (!operatorAccessLevels.packages.bizPrint.accessToAny) {
            return;
        }
        const response = await Api.getPrintApplicationToken();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            CacheService.setPrintApplicationToken(response.data);
        }
    }, [isMounted, operatorAccessLevels.packages.bizPrint.accessToAny]);

    /**
     * As soon as the component mounts:
     * - fetches the biz print application token.
     */
    useEffect(() => {
        getBizPrintApplicationToken().then();
    }, [getBizPrintApplicationToken]);

    const panelSidebar = useMemo(() => <PanelSidebar/>, []);
    const panelAppbar = useMemo(() => <PanelAppbar/>, []);
    const panelDrawers = useMemo(() => <PanelDrawers/>, [])
    const panelInformationFetcher = useMemo(() => <PanelInformationFetcher/>, []);

    const shouldNotShowScroll = useMemo(() => {
        const matchSingle = matchPath(location.pathname, {
            path: routes.lookup.productInformation,
            exact: true,
        });
        const matchPartSupportProcess = matchPath(location.pathname, {
            path: routes.warrantyAndReturn.settings.process,
            exact: true,
        })
        return matchSingle || matchPartSupportProcess;
    }, [location.pathname])

    const shouldNotHavePadding = useMemo(() => {
        const match = matchPath(location.pathname, {
            path: [
                routes.sales.invoices,
                routes.sales.quotes,
                routes.sales.salesOrder,
                routes.sales.backOrder,
                routes.accounting.invoices,
                routes.sales.credits,
                routes.sales.invoices,
                routes.sales.quotes,
                routes.sales.salesOrder,
                routes.sales.backOrder,
                routes.accounting.invoices,
                routes.sales.credits,
                routes.customer.sales.invoices.base,
                routes.customer.sales.invoices.single,
                routes.customer.sales.quotes.base,
                routes.customer.sales.quotes.single,
                routes.customer.sales.salesOrder.base,
                routes.customer.sales.salesOrder.single,
                routes.customer.sales.backOrder.base,
                routes.customer.sales.backOrder.single,
                routes.customer.accounting.invoices.base,
                routes.customer.accounting.invoices.single,
                routes.customer.sales.credits.base,
                routes.customer.sales.credits.single,
                routes.analytics.dashboards,
            ],
            exact: true,
        });
        return !!match;
    }, [location.pathname])

    const forcePanelLoading = useMemo(() => {
        const map = {
            [routes.customer.base]: !defaultCustomer?.id,
            [routes.vendor.base]: !defaultVendor?.id,
        }
        const matchedEntry = Object.entries(map).find(([key]) => matchPath(location.pathname, {path: key, exact: false}));
        return matchedEntry?.[1] ?? false;
    }, [defaultCustomer?.id, defaultVendor?.id, location.pathname])

    return (
        <>
            <Switch>
                {
                    operatorAccessLevels.packages.base.versionHistory &&
                    <Route path={routes.versions} exact>
                        <VersionsView/>
                    </Route>
                }
                <Route path={routes.landing}>
                    <SuggestiveSellingProvider>
                        <Panel
                            sidebar={panelSidebar}
                            appbar={panelAppbar}
                            drawers={panelDrawers}
                            persistentChildren={panelInformationFetcher}
                            forceLoading={forcePanelLoading}
                            classNames={{
                                main: classnames({'no-scrollbar': shouldNotShowScroll, 'p-0': shouldNotHavePadding}),
                                layout: classnames({'demo-env-layout': EnvService.showInDemoEnvironment || EnvService.showInMainEnvironment}),
                            }}
                        >
                            <PanelRoutes/>
                        </Panel>
                        {
                            !inNotebookRoute &&
                            (operatorAccessLevels.packages.taskNote.notes || operatorAccessLevels.packages.taskNote.tasks) &&
                            <PanelFloatingActionButton/>
                        }
                        <PanelDialogsUnderPersonalConfigurations/>
                        {operatorAccessLevels.packages.taskNote.tasks && <TaskMoreInfoDialog/>}
                        {operatorAccessLevels.packages.taskNote.tasks && <UpsertTaskDialog/>}
                        {(operatorAccessLevels.packages.taskNote.notes || operatorAccessLevels.packages.base.manageVendorNotes) && <UpsertNoteDialog/>}
                        {operatorAccessLevels.packages.sales.quickOrder && <QuickOrderDialog/>}
                    </SuggestiveSellingProvider>
                </Route>
            </Switch>
        </>
    )
}

const PanelInformationFetcher = () => {
    const {setLoading: setPanelLoading} = usePanel();
    const {toggleShow: toggleShowNotebooksAction} = useConfigureNotebook('panel-routes');
    const dispatch = useAppDispatch();
    const history = useHistory();
    const isMounted = useIsMounted();
    const historyRef = React.useRef(history);
    historyRef.current = history;
    const location = useLocation();
    const locationRef = React.useRef(location);
    locationRef.current = location;

    const customerViewsMatch = useRouteMatch({path: routes.customer.base, exact: false});
    const customerId = useMemo(() => customerViewsMatch?.params.customerId, [customerViewsMatch]);

    const vendorViewsMatch = useRouteMatch({path: routes.vendor.base, exact: false});
    const vendorId = useMemo(() => vendorViewsMatch?.params.vendorId, [vendorViewsMatch]);

    /**
     * Gets the user profile information and sets the firstname lastname in the redux profile info
     * @type {(function(*, *): Promise<void>)}
     */
    const getCustomerInformation = useCallback(async (customerId, abortController) => {
        const response = await Api.getCustomerSimpleInfoById(customerId);
        if (response?.resultFlag && !abortController?.signal.aborted) {
            dispatch(ReduxActions.customerInformation.setCustomerInformation({
                id: response.data.id,
                name: response.data.name,
                code: response.data.code,
            }));
            toggleShowNotebooksAction(true);
        } else if (!abortController?.signal.aborted) {
            historyRef.current.replace(locationRef.current.pathname.concat(routes.error.accessDeniedForSection));
        }
        if (!isMounted())
            return;
        if (!abortController?.signal.aborted) {
            setPanelLoading(false);
        }
    }, [dispatch, isMounted, setPanelLoading, toggleShowNotebooksAction])

    /**
     * With each change in the [params.customerId]:
     * - get the customer profile information
     */
    useLayoutEffect(() => {
        if (!!customerId) {
            setPanelLoading(true);
            toggleShowNotebooksAction(false);
            if (Number.isNaN(Number(customerId))) {
                setPanelLoading(false);
                toggleShowNotebooksAction(true);
                historyRef.current.replace(locationRef.current.pathname.concat(routes.error.accessDeniedForSection));
            } else {
                const abortController = new AbortController();
                void getCustomerInformation(customerId, abortController);
                return () => abortController.abort();
            }
        } else {
            dispatch(ReduxActions.customerInformation.clearCustomerInformation());
        }
    }, [dispatch, getCustomerInformation, customerId, setPanelLoading, toggleShowNotebooksAction])

    /**
     * Gets the vendor profile information and sets the name and code in the redux profile info
     */
    const getVendorInformation = useCallback(async (vendorId, abortController) => {
        const response = await Api.getVendorV2(Number(vendorId));
        if (response?.resultFlag && !abortController?.signal.aborted) {
            dispatch(ReduxActions.vendorInformation.setVendorInformation({
                id: response.data.id,
                name: response.data.name,
                code: response.data.code,
                email: response.data.email,
                phone: response.data.phone,
                paymentTermsCode: response.data.paymentTermsCode,
                statusId: response.data.statusId,
                statusName: response.data.statusName,
            }));
            toggleShowNotebooksAction(true);
        } else if (!abortController?.signal.aborted) {
            historyRef.current.replace(locationRef.current.pathname.concat(routes.error.accessDeniedForSection));
        }
        if (!isMounted())
            return;
        if (!abortController?.signal.aborted) {
            setPanelLoading(false);
        }
    }, [dispatch, setPanelLoading, toggleShowNotebooksAction, isMounted])

    /**
     * With each change in the [params.vendorId]:
     * - get the vendor profile information
     */
    useLayoutEffect(() => {
        if (!!vendorId) {
            setPanelLoading(true);
            toggleShowNotebooksAction(false);
            if (Number.isNaN(Number(vendorId))) {
                setPanelLoading(false);
                toggleShowNotebooksAction(true);
                historyRef.current.replace(locationRef.current.pathname.concat(routes.error.accessDeniedForSection));
            } else {
                const abortController = new AbortController();
                void getVendorInformation(vendorId, abortController);
                return () => abortController.abort();
            }
        } else {
            dispatch(ReduxActions.vendorInformation.clearVendorInformation());
        }
    }, [dispatch, getVendorInformation, vendorId, setPanelLoading, toggleShowNotebooksAction])

    /**
     * If there is no [customerId] and [vendorId], then show the panel and show the
     */
    useLayoutEffect(() => {
        if (!customerId && !vendorId) {
            toggleShowNotebooksAction(true);
            setPanelLoading(false);
        }
    }, [customerId, setPanelLoading, toggleShowNotebooksAction, vendorId]);

    return (
        <></>
    );
}

const PanelRoutes = () => {
    const operatorAccessLevels = useAccessLevels();

    return (
        <>
            <Switch>
                <Redirect from={routes.dashboard} to={routes.analytics.systemDashboards} exact/>
                <Redirect from={routes.reports} to={routes.analytics.systemReports} exact/>
                {/*Routes for operator*/}
                {
                    operatorAccessLevels.packages.base.editOwnProfile &&
                    <Route path={routes.operatorProfile} exact>
                        <OperatorProfileView/>
                    </Route>
                }
                {
                    operatorAccessLevels.packages.base.editPartnerProfile &&
                    <Route path={routes.partnerProfile} exact>
                        <PartnerProfileView/>
                    </Route>
                }
                <Route path={routesLists.lookup} exact>
                    <LookupV2Views/>
                </Route>
                <Route path={[...routesLists.warrantyAndReturn, ...routesLists.warrantyAndReturnOld, routes.drivers]} exact>
                    <PartSupportRequestViews/>
                </Route>
                <Route path={routesLists.ncr} exact>
                    <NCRViews/>
                </Route>
                {
                    operatorAccessLevels.packages.bizForm.accessToMenuItems &&
                    <Route path={routesLists.forms} exact>
                        <FormManagementViews/>
                    </Route>
                }
                {
                    (
                        operatorAccessLevels.packages.bizPoint.loyaltyDashboard
                        || operatorAccessLevels.packages.bizPoint.loyaltyBuyBiz
                        || operatorAccessLevels.packages.bizPoint.loyaltyEarningRules
                        || operatorAccessLevels.packages.bizPoint.loyaltyCustomers
                        || operatorAccessLevels.packages.bizPoint.loyaltySettings
                    ) &&
                    <Route path={routesLists.bizPointLoyalty} exact>
                        <BizPointLoyaltyViews/>
                    </Route>
                }
                {
                    (
                        operatorAccessLevels.packages.bizPoint.adminReviewQueue
                        || operatorAccessLevels.packages.bizPoint.adminManualAdjustment
                        || operatorAccessLevels.packages.bizPoint.adminBusinessConfig
                        || operatorAccessLevels.packages.bizPoint.adminLiabilityReport
                        || operatorAccessLevels.packages.bizPoint.adminAuditLog
                        || operatorAccessLevels.packages.bizPoint.adminPurchaseConfirm
                        || operatorAccessLevels.packages.bizPoint.adminWalletSuspend
                    ) &&
                    <Route path={routesLists.bizPointAdmin} exact>
                        <BizPointAdminViews/>
                    </Route>
                }
                <Route path={routesLists.customers} exact>
                    <CustomerViews/>
                </Route>
                <Route path={routesLists.operators} exact>
                    <OperatorsViews/>
                </Route>
                <Route path={[
                    routes.sales.base,
                    routes.sales.quotes,
                    routes.sales.salesOrder,
                    routes.sales.backOrder,
                    routes.sales.invoices,
                    routes.sales.credits,
                    ...routesLists.salesOld,
                ]} exact>
                    <OrderViews allowPayments={false}/>
                </Route>
                {
                    operatorAccessLevels.packages.sales.promotions &&
                    <Route path={routesLists.promotions} exact>
                        <PromotionsView/>
                    </Route>
                }
                <Route path={routesLists.accounting} exact>
                    <AccountingViews/>
                </Route>
                <Route path={routesLists.settings} exact>
                    <SettingViews/>
                </Route>
                <Route path={[
                    routes.userNotifications.base,
                    routes.userNotifications.list,
                    routes.userNotifications.accessLevels,
                    routes.userNotifications.subscriptions,
                ]} exact>
                    <UserNotificationsView/>
                </Route>
                <Route path={routesLists.content} exact>
                    <ContentViews/>
                </Route>
                {
                    operatorAccessLevels.packages.sales.salesPerson &&
                    <Route path={[routes.salesPerson.base, routes.salesPerson.dashboard]} exact>
                        <SalesPersonViews/>
                    </Route>
                }
                <Route path={routesLists.products} exact>
                    <ProductsView/>
                </Route>
                <Route path={routesLists.inventory} exact>
                    <InventoryViews/>
                </Route>
                <Route path={routesLists.printing} exact>
                    <PrintingView/>
                </Route>
                <Route path={routesLists.userPlatform} exact>
                    <UserPlatformViews/>
                </Route>
                <Route path={routesLists.analytics} exact>
                    <AnalyticsViews/>
                </Route>
                {/*Routes for Customer*/}
                <Route path={[
                    routes.customer.base,
                    ...routesLists.customer.generalInformation,
                ]} exact>
                    <CustomerGeneralInformationViews/>
                </Route>
                {
                    operatorAccessLevels.packages.bizForm.formResponsesList &&
                    <Route path={routes.customer.forms.responses} exact>
                        <CustomerFormResponsesPage/>
                    </Route>
                }
                {
                    operatorAccessLevels.packages.sales.customerPriceMatrices &&
                    <Route path={routes.customer.priceMatrix} exact>
                        <PriceMatricesListView/>
                    </Route>
                }
                {
                    operatorAccessLevels.packages.taskNote.notes &&
                    <Route path={routes.customer.notes} exact>
                        <CustomerNotesView/>
                    </Route>
                }
                <Route path={[
                    routes.customer.accounting.base,
                    routes.customer.accounting.availableCredits,
                    routes.customer.accounting.accountReceivableTransactions,
                    routes.customer.accounting.invoices.single,
                    routes.customer.accounting.invoices.base,
                ]} exact>
                    <CustomerAccountingViews/>
                </Route>
                <Route path={[
                    routes.customer.sales.base,
                    routes.customer.sales.quotes.base,
                    routes.customer.sales.quotes.single,
                    routes.customer.sales.salesOrder.base,
                    routes.customer.sales.salesOrder.single,
                    routes.customer.sales.backOrder.base,
                    routes.customer.sales.backOrder.single,
                    routes.customer.sales.invoices.base,
                    routes.customer.sales.invoices.single,
                    routes.customer.sales.credits.single,
                    routes.customer.sales.credits.base,
                    ...routesLists.customerSalesOld,
                ]} exact>
                    <OrderViews allowPayments={false}/>
                </Route>
                {
                    operatorAccessLevels.packages.sales.customerShoppingCart &&
                    <Route path={routes.customer.sales.shoppingCart} exact>
                        <CustomerShoppingCartView/>
                    </Route>
                }
                {
                    operatorAccessLevels.packages.sales.manageCustomerPaymentMethods &&
                    <Route path={routes.customer.cardSummary.creditCards} exact>
                        <CustomerCreditCardSummaryView/>
                    </Route>
                }
                {
                    operatorAccessLevels.packages.sales.customerGiftCards &&
                    <Route path={routes.customer.cardSummary.giftCards} exact>
                        <CustomerGiftCardSummariesView/>
                    </Route>
                }
                {/*Routes for vendor*/}
                {
                    operatorAccessLevels.packages.base.viewVendors &&
                    <Route path={routes.vendors} exact>
                        <VendorsListView/>
                    </Route>
                }
                <Route path={routesLists.vendorPurchases} exact>
                    <PurchaseViews/>
                </Route>
                {
                    (operatorAccessLevels.packages.base.viewVendorAccountInfo ||
                        operatorAccessLevels.packages.base.viewVendorNotes ||
                        operatorAccessLevels.packages.base.viewVendorProducts) &&
                    <Route path={routesLists.vendor} exact>
                        <VendorViews/>
                    </Route>
                }
                {/*Common Routes*/}
                <Route path={routes.landing} exact/>
                {
                    operatorAccessLevels.packages.notifications.manageList &&
                    <Route path={routes.notificationManagement} exact>
                        <NotificationManagementViews/>
                    </Route>
                }
                {
                    operatorAccessLevels.packages.taskNote.notebook &&
                    <Route path={routes.notebook} exact>
                        <NotebookView/>
                    </Route>
                }
                <Route path={[...routesLists.shippingFees, ...routesLists.customer.shippingFees]} exact>
                    <ShippingFeesViews/>
                </Route>
                <Route path={routesLists.damageControl} exact>
                    <DamageControlViews/>
                </Route>
                {
                    operatorAccessLevels.packages.support.contactUsMessages &&
                    <Route path={routesLists.support} exact>
                        <SupportViews/>
                    </Route>
                }
                <Route path={routesLists.purchase} exact>
                    <PurchaseViews/>
                </Route>
                <Route path={routesLists.allWithServerErrorSection} exact>
                    <ServerErrorView/>
                </Route>
                <Route path={routesLists.allWithAccessDeniedSection} exact>
                    <AccessDeniedView/>
                </Route>
                <Redirect to={routes.error.notFound}/>
            </Switch>
        </>
    );
}

const PanelDialogsUnderPersonalConfigurations = () => {
    const [birthdayOpen, setBirthdayOpen] = useState(false);
    /**
     * Stores the `onClose` callback supplied by the queue so that closing the
     * Birthday dialog (which is prop-driven) can advance the sequence.
     */
    const birthdayOnCloseRef = useRef(null);

    const operatorInformation = useOperatorInformation();
    const operatorAccessLevels = useAccessLevels();
    const personalPanelConfig = usePersonalPanelConfigurations();
    const isMounted = useIsMounted();
    const dispatch = useDispatch();

    /**
     * Handles cross-tab storage events so that a dialog closed in one tab is
     * also closed in every other open tab.
     */
    const determineStorageEvent = useCallback((event) => {
        switch (event.key) {
            case LocalStorageService.eventKeys.whatsNewDialog:
                if (event.newValue === 'true') {
                    dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.whatsNew, {open: false}))
                }
                break;
            case LocalStorageService.eventKeys.welcomeToPanelDialog:
                if (event.newValue === 'true') {
                    dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.welcomePanelTaskDialog, {open: false}))
                }
                break;
            default:
                return;
        }
    }, [dispatch]);

    useEffect(() => {
        window.addEventListener('storage', determineStorageEvent);
        return () => window.removeEventListener('storage', determineStorageEvent);
    }, [determineStorageEvent]);

    /**
     * Fetches the latest version and opens the What's New dialog if there is
     * new content to display. When the API returns nothing new the `onClose`
     * callback is invoked immediately so the queue advances without showing the
     * dialog.
     */
    const openWhatsNewDialog = useCallback(async (onClose) => {
        const response = await Api.getLatestApplicationsVersion();
        if (!isMounted()) return;
        if (response?.resultFlag && response?.data?.length) {
            dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.whatsNew, {
                open: true,
                data: response.data,
                onClose,
            }));
        } else {
            onClose();
        }
    }, [dispatch, isMounted]);

    /**
     * Ordered queue of dialogs shown on panel mount.
     *
     * Each entry is evaluated against three eligibility gates before being
     * displayed:
     *   1. `accessLevelAllowed`   — operator access-level permission
     *   2. `personalConfigAllowed` — the operator's saved personal panel config
     *   3. `cacheAllowed()`        — CacheService / localStorage time-based guard
     *
     * The `open` function receives an `onClose` that MUST be called when the
     * dialog finishes so the queue advances to the next entry.
     */
    const dialogQueue = useMemo(() => [
        {
            id: 'birthday',
            accessLevelAllowed: operatorAccessLevels.packages.base.birthdayDialog,
            personalConfigAllowed: personalPanelConfig.showHappyBirthdayDialog,
            cacheAllowed: () => CacheService.shouldOpenBirthdayDialog,
            open: (onClose) => {
                birthdayOnCloseRef.current = onClose;
                setBirthdayOpen(true);
            },
        },
        {
            id: 'whats-new',
            accessLevelAllowed: operatorAccessLevels.packages.base.versionHistory,
            personalConfigAllowed: personalPanelConfig.showWhatsNewDialog,
            cacheAllowed: () => CacheService.canOpenWhatsNewDialog,
            open: openWhatsNewDialog,
        },
        {
            id: 'welcome-panel',
            accessLevelAllowed: operatorAccessLevels.packages.taskNote.welcomeToPanelTasksDialog,
            personalConfigAllowed: personalPanelConfig.showWelcomeToPanelTasksDialog,
            cacheAllowed: () => CacheService.canOpenWelcomeToPanelDialog,
            open: (onClose) => {
                dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.welcomePanelTaskDialog, {
                    open: true,
                    onClose,
                }));
            },
        },
    ], [
        operatorAccessLevels.packages.base.birthdayDialog,
        operatorAccessLevels.packages.base.versionHistory,
        operatorAccessLevels.packages.taskNote.welcomeToPanelTasksDialog,
        personalPanelConfig.showHappyBirthdayDialog,
        personalPanelConfig.showWhatsNewDialog,
        personalPanelConfig.showWelcomeToPanelTasksDialog,
        openWhatsNewDialog,
        dispatch,
    ]);

    /**
     * Only start the queue after the personal panel config has finished loading
     * so that saved preferences are honored rather than the `true` defaults
     * that are in place during the async fetch.
     */
    usePanelDialogQueue(dialogQueue, !personalPanelConfig.loading);

    return (
        <>
            {
                operatorAccessLevels.packages.base.versionHistory &&
                <WhatsNewDialog/>
            }
            {
                operatorAccessLevels.packages.taskNote.welcomeToPanelTasksDialog &&
                <WelcomeToPanelTaskDialog/>
            }
            {
                operatorAccessLevels.packages.base.birthdayDialog &&
                <BirthdayDialog
                    open={birthdayOpen}
                    close={() => {
                        setBirthdayOpen(false);
                        birthdayOnCloseRef.current?.();
                        birthdayOnCloseRef.current = null;
                    }}
                    name={operatorInformation?.name ?? ""}
                />
            }
        </>
    );
}

export default PanelViews;
